import type {
  IIntentStatusResponse,
  ISubmitConfigurationResponse,
} from 'services/ExternalService';
import { LogyxErrorId, readLogyxError } from 'services/api/errors';
import type { IConfiguredProductData } from './hooks';

// Following an intent's submission (logix-core docs/ordering.md, "Intents";
// docs/BP-1219-ordering.md). Logyx core sends it in a task and settles it as
// success, failed (the supplier's reason in `message`; it can be submitted
// again) or unknown (it may already be at the supplier; final, and checked by
// Logyx). Nothing is submitted again automatically: after a lost answer, a
// second submission could add the lines twice.

// Asked every second for the first 10 seconds, then every 3.
export const POLL_INTERVAL_MS = 1000;
export const SLOW_POLL_INTERVAL_MS = 3000;
export const FAST_POLLING_MS = 10 * 1000;
// From here the overlay says a submission can take a few minutes: a large one
// to NAV takes up to five.
export const SLOW_SUBMISSION_MS = 60 * 1000;
// Logyx core's outcome job settles a submission left processing within about
// 8 minutes. Past 10 the page stops waiting and says it is being checked.
export const MAX_POLLING_MS = 10 * 60 * 1000;
// After a lost answer: a submission still pending this long never arrived.
export const NOT_RECEIVED_AFTER_MS = 5 * 1000;

export type SubmissionOutcomeKind = 'being-checked' | 'failed' | 'not-received';

export class SubmissionOutcomeError extends Error {
  kind: SubmissionOutcomeKind;
  // Logyx core's own text (translated by it), when it gave one.
  serverMessage: string | null;

  constructor(kind: SubmissionOutcomeKind, serverMessage: string | null) {
    super(serverMessage || kind);
    this.kind = kind;
    this.serverMessage = serverMessage;
  }
}

export const submissionOutcomeOf = (
  error: unknown
): SubmissionOutcomeError | null =>
  error &&
  typeof error === 'object' &&
  'kind' in error &&
  'serverMessage' in error
    ? (error as SubmissionOutcomeError)
    : null;

// The two calls to Logyx core (services/ExternalService), passed in so that this
// file holds only the decisions.
export interface ISubmissionServices {
  submit: (
    data: IConfiguredProductData,
    base64Metadata?: string
  ) => Promise<ISubmitConfigurationResponse>;
  getStatus: () => Promise<IIntentStatusResponse>;
}

export interface ISubmissionClock {
  now: () => number;
  sleep: (ms: number) => Promise<void>;
}

const realClock: ISubmissionClock = {
  now: () => Date.now(),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

// A poll that fails is not a failed submission: the next one runs as planned.
// Only an answer that can never change ends the wait.
const endsTheWait = (error: unknown) => {
  const status = readLogyxError(error).status;
  return status === 401 || status === 403 || status === 404;
};

export const waitForSubmission = async (
  services: ISubmissionServices,
  onSlow: () => void,
  afterLostAnswer = false,
  clock: ISubmissionClock = realClock
): Promise<IIntentStatusResponse> => {
  const startedAt = clock.now();
  let isSlow = false;

  for (;;) {
    const elapsed = clock.now() - startedAt;
    if (elapsed >= MAX_POLLING_MS) {
      throw new SubmissionOutcomeError('being-checked', null);
    }
    if (!isSlow && elapsed >= SLOW_SUBMISSION_MS) {
      isSlow = true;
      onSlow();
    }

    let answer: IIntentStatusResponse | null = null;
    try {
      answer = await services.getStatus();
    } catch (error) {
      if (endsTheWait(error)) throw error;
    }

    switch (answer?.status) {
      case 'success':
        return answer;
      case 'failed':
        throw new SubmissionOutcomeError('failed', answer.message);
      case 'unknown':
        throw new SubmissionOutcomeError('being-checked', answer.message);
      case 'pending':
        if (afterLostAnswer && elapsed >= NOT_RECEIVED_AFTER_MS) {
          throw new SubmissionOutcomeError('not-received', null);
        }
        break;
    }

    await clock.sleep(
      elapsed < FAST_POLLING_MS ? POLL_INTERVAL_MS : SLOW_POLL_INTERVAL_MS
    );
  }
};

export const submitAndWait = async (
  services: ISubmissionServices,
  data: IConfiguredProductData,
  base64Metadata: string | undefined,
  onSlow: () => void,
  clock: ISubmissionClock = realClock
): Promise<ISubmitConfigurationResponse | IIntentStatusResponse> => {
  let submitted: ISubmitConfigurationResponse;
  try {
    submitted = await services.submit(data, base64Metadata);
  } catch (error) {
    const answer = readLogyxError(error);
    // An intent whose outcome is unknown is never submitted again (409).
    if (answer.errorId === LogyxErrorId.ORDER_OUTCOME_UNKNOWN) {
      const first = answer.errors[0];
      throw new SubmissionOutcomeError(
        'being-checked',
        (first && typeof first === 'object' && first.message) || null
      );
    }
    // The answer was lost (a proxy's page, a network error): the submission
    // may have arrived. Ask Logyx core how it stands, instead of failing or
    // submitting again.
    if (!answer.isLogyxAnswer) {
      return waitForSubmission(services, onSlow, true, clock);
    }
    throw error;
  }

  // Already submitted successfully (a repeat of a finished submission).
  if (submitted?.status === 'success') return submitted;
  return waitForSubmission(services, onSlow, false, clock);
};
